import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  autoExposureConstraints, captureClock, describeOpenError, describeSize, deviceGone, MIN_LONG_SIDE_PX,
  offersAutoExposure, pickDevice, RESET_SETTLE_MS, resetConstraints, startTimedOut,
  tooSmall, videoConstraints, type CaptureClock, type CaptureTiming,
} from '../camera';

const DEVICE_KEY = 'stitch-lane.printer-camera';

export interface Still {
  blob: Blob; width: number; height: number;
  /** How long the shot took, for the upload request to report. */
  timing?: CaptureTiming;
}

/** A one-click shot that needs the operator to look first; see `shootBlind`. */
export class NeedsPreview extends Error {}

/**
 * The camera on the printer's lid, read straight from the workstation.
 *
 * One per page, shared by every panel that shoots with it: two getUserMedia
 * streams on one USB camera at different sizes is a "device in use" waiting
 * to happen, and the Printers page has two panels (lens, calibration).
 *
 * A still is taken with ImageCapture where the browser has it, because that
 * asks the driver for a photograph at the sensor's full size; otherwise it is
 * the current video frame, which is only as large as the stream the browser
 * agreed to. Either way the size is checked before upload, since a browser
 * that negotiated 1080p does so silently.
 *
 * Exposure is left on automatic (see `autoExposureConstraints`).
 */
export function usePrinterCamera() {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | null>(() => remembered());
  const [error, setError] = useState<string | null>(null);
  // Held in a ref as well, so the camera is released at once: reopening the
  // same device before the old stream stops fails as "device in use".
  const open = useRef<MediaStream | null>(null);

  const stop = useCallback(async () => {
    const closing = open.current;
    open.current = null;
    setStream(null);
    await release(closing);
  }, []);
  useEffect(() => () => { void stop(); }, [stop]);
  // Closing the tab skips the unmount; this is the best that can be done then,
  // and `openCamera` recovers if it was not enough.
  useEffect(() => {
    const hide = () => { void release(open.current); };
    window.addEventListener('pagehide', hide);
    return () => window.removeEventListener('pagehide', hide);
  }, []);

  const start = useCallback(async (id: string | null) => {
    setError(null);
    await stop();
    try {
      let opened: MediaStream;
      try {
        opened = await openCamera(id);
      } catch (e) {
        // The remembered camera is gone: open whichever is there, and the
        // list below lets the right one be picked if that is not it.
        if (id === null || !deviceGone(e)) throw e;
        opened = await openCamera(null);
      }
      // Labels and ids are only listed once permission is granted.
      const cameras = (await navigator.mediaDevices.enumerateDevices())
        .filter((d) => d.kind === 'videoinput');
      setDevices(cameras);
      const actual = opened.getVideoTracks()[0]?.getSettings().deviceId ?? null;
      const chosen = actual ?? pickDevice(cameras, id);
      setDeviceId(chosen);
      remember(chosen);
      open.current = opened;
      setStream(opened);
    } catch (e) {
      setError(`Could not open the camera: ${describeOpenError(e)}.`);
    }
  }, [stop]);

  /** A full-size JPEG from the preview, or an error saying why not. */
  const shoot = useCallback(async (video: HTMLVideoElement,
                                   clock?: CaptureClock): Promise<Still> => {
    const track = open.current?.getVideoTracks()[0];
    if (!track) throw new Error('the camera is off');
    return shootFrom(track, video, clock);
  }, []);

  /**
   * Turn the camera on, take one full-size photo and turn it off again, with
   * nothing on screen (for a printer set to its lid camera). A camera that is
   * not the one this workstation last used throws `NeedsPreview`, and the
   * panel opens the preview so the right one can be picked.
   */
  const shootBlind = useCallback(async (clock?: CaptureClock): Promise<Still> => {
    if (open.current) throw new Error('the preview is on; shoot from it');
    setError(null);
    let opened: MediaStream | null = null;
    try {
      const id = remembered();
      try {
        opened = await openCamera(id, () => { if (clock) clock.timing.retried = true; });
      } catch (e) {
        if (id === null || !deviceGone(e)) {
          throw new Error(`Could not open the camera: ${describeOpenError(e)}.`);
        }
        throw new NeedsPreview('The camera this workstation last used is not '
          + 'there. Pick the right one in the preview.');
      }
      clock?.lap('open');
      const track = opened.getVideoTracks()[0];
      if (!track) throw new Error('the camera gave no picture');
      const video = await hiddenVideo(opened);
      clock?.lap('settle');
      return await shootFrom(track, video, clock);
    } finally {
      await release(opened);
      clock?.lap('release');
    }
  }, []);

  return { stream, devices, deviceId, error, start, stop, shoot, shootBlind };
}

export type PrinterCameraHandle = ReturnType<typeof usePrinterCamera>;

/**
 * A live preview and a button per thing to shoot.
 *
 * `onPhoto` does the upload; the button stays busy until it settles, so a
 * second click cannot race the first for the same signed URL.
 */
export function PrinterCamera({ camera, actions, onPhoto, children, quick = false }: {
  camera: PrinterCameraHandle;
  actions: { key: string; label: string }[];
  onPhoto: (key: string, photo: Still) => Promise<void>;
  /** What to do before shooting; shown above the preview. */
  children?: ReactNode;
  /**
   * Shoot with the preview off: each button turns the camera on for one photo
   * (see `shootBlind`). For a printer set to its lid camera, which always sees
   * the same bed from the same place. The preview is still a button away.
   */
  quick?: boolean;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [last, setLast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What was last sent, so a shot taken without the preview is still seen.
  const [sent, setSent] = useState<string | null>(null);
  useEffect(() => () => { if (sent) URL.revokeObjectURL(sent); }, [sent]);
  const { stream, devices, deviceId } = camera;

  useEffect(() => {
    if (video.current) video.current.srcObject = stream;
  }, [stream]);

  async function press(key: string, label: string) {
    const blind = !stream;
    if (!blind && !video.current) return;
    setBusy(key);
    setError(null);
    try {
      const clock = captureClock(blind ? 'blind' : 'preview');
      const shot = blind
        ? await camera.shootBlind(clock)
        : await camera.shoot(video.current!, clock);
      const photo = { ...shot, timing: { ...clock.done(), width: shot.width, height: shot.height } };
      await onPhoto(key, photo);
      setLast(`${label}: sent ${describeSize(photo.width, photo.height)}`);
      setSent(URL.createObjectURL(photo.blob));
    } catch (e) {
      setError((e as Error).message);
      // Not a fault to retry blind: open the preview so it can be looked at.
      if (e instanceof NeedsPreview) void camera.start(deviceId);
    } finally {
      setBusy(null);
    }
  }

  const settings = stream?.getVideoTracks()[0]?.getSettings();

  return (
    <div className="stack">
      {children}
      {!stream && quick ? (
        <div className="button-row">
          {actions.map(({ key, label }) => (
            <button key={key} className="primary" disabled={busy !== null}
                    onClick={() => void press(key, label)}>
              {busy === key ? 'Capturing…' : label}
            </button>
          ))}
          <button onClick={() => void camera.start(deviceId)} disabled={busy !== null}>
            Show live preview
          </button>
        </div>
      ) : !stream ? (
        <div className="button-row">
          <button className="primary" onClick={() => void camera.start(deviceId)}>
            Turn on the printer camera
          </button>
        </div>
      ) : (
        <>
          <video ref={video} className="camera-preview" autoPlay muted playsInline />
          {devices.length > 1 && (
            <label>
              Camera{' '}
              <select value={deviceId ?? ''}
                      onChange={(e) => void camera.start(e.target.value)}>
                {devices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${i + 1}`}
                  </option>
                ))}
              </select>
            </label>
          )}
          {settings?.width && settings.height && (
            <p className="muted">Preview {describeSize(settings.width, settings.height)}</p>
          )}
          <div className="button-row">
            {actions.map(({ key, label }) => (
              <button key={key} className="primary" disabled={busy !== null}
                      onClick={() => void press(key, label)}>
                {busy === key ? 'Capturing…' : label}
              </button>
            ))}
            <button onClick={() => void camera.stop()} disabled={busy !== null}>
              {quick ? 'Hide preview' : 'Turn off'}
            </button>
          </div>
        </>
      )}
      {last && <p className="muted">{last}</p>}
      {sent && (
        <img className="sent-still" src={sent} alt="The photo just sent" />
      )}
      {(error ?? camera.error) && <p className="error">{error ?? camera.error}</p>}
    </div>
  );
}

/**
 * getUserMedia at full size. If the camera is found but will not start in
 * time (see `startTimedOut`), open it small, put its exposure back on
 * automatic, close it and try once more.
 */
async function openCamera(id: string | null,
                          onRetry?: () => void): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia(videoConstraints(id));
  } catch (e) {
    if (!startTimedOut(e)) throw e;
    onRetry?.();
    try {
      await release(await navigator.mediaDevices.getUserMedia(resetConstraints(id)));
    } catch { /* the second try says what is wrong */ }
    await new Promise((r) => window.setTimeout(r, RESET_SETTLE_MS));
    return navigator.mediaDevices.getUserMedia(videoConstraints(id));
  }
}

/**
 * Put the camera's exposure back on automatic, then let it go.
 *
 * Nothing here sets a manual exposure, but the camera keeps one in its own
 * hardware after whatever set it lets go, until it loses power: the exposure
 * lock this page used to have left long ones there that stopped the camera
 * starting at all (`startTimedOut`), and another app can do the same.
 */
async function release(stream: MediaStream | null): Promise<void> {
  if (!stream) return;
  const track = stream.getVideoTracks()[0];
  if (track && offersAutoExposure(track.getCapabilities?.())) {
    await track.applyConstraints(autoExposureConstraints()).catch(() => {});
  }
  stream.getTracks().forEach((t) => t.stop());
}

// -- the still ---------------------------------------------------------------

/** A full-size JPEG, or an error saying why not. */
async function shootFrom(track: MediaStreamTrack, video: HTMLVideoElement,
                         clock?: CaptureClock): Promise<Still> {
  const photo = await still(track, video, clock);
  if (tooSmall(photo.width, photo.height)) {
    throw new Error(
      `the camera gave ${describeSize(photo.width, photo.height)}, below the `
      + `${MIN_LONG_SIDE_PX} px needed. Check it is on a USB 3 port and not `
      + 'open in another program.');
  }
  return photo;
}

/**
 * How long a blind shot lets the camera run before the photo. Automatic
 * exposure starts from wherever it was and takes about a second to settle on
 * the bed; with the preview on, the operator's own look gives it that time.
 */
const BLIND_SETTLE_MS = 1500;

/**
 * An off-screen video on the stream, playing, after the settle above: the
 * fallback still is read from it.
 */
async function hiddenVideo(stream: MediaStream): Promise<HTMLVideoElement> {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  await video.play();
  await new Promise((r) => window.setTimeout(r, BLIND_SETTLE_MS));
  return video;
}


/** Not in TypeScript's DOM library yet; only what is used here. */
interface ImageCaptureLike {
  getPhotoCapabilities(): Promise<{
    imageWidth?: { max?: number }; imageHeight?: { max?: number };
  }>;
  takePhoto(settings?: { imageWidth?: number; imageHeight?: number }): Promise<Blob>;
}
type ImageCaptureCtor = new (track: MediaStreamTrack) => ImageCaptureLike;

async function still(track: MediaStreamTrack, video: HTMLVideoElement,
                     clock?: CaptureClock): Promise<Still> {
  const Ctor = (window as unknown as { ImageCapture?: ImageCaptureCtor }).ImageCapture;
  if (Ctor) {
    try {
      const capture = new Ctor(track);
      const caps = await capture.getPhotoCapabilities();
      const blob = await capture.takePhoto({
        imageWidth: caps.imageWidth?.max, imageHeight: caps.imageHeight?.max,
      });
      clock?.lap('photo');
      const photo = await asJpeg(await createImageBitmap(blob), blob);
      clock?.lap('encode');
      if (clock) clock.timing.method = 'imagecapture';
      return photo;
    } catch {
      // Some drivers list ImageCapture and then fail it; the frame will do.
    }
  }
  const frame = await createImageBitmap(video);
  // A failed takePhoto is in here too; that is worth seeing, not hiding.
  clock?.lap('photo');
  const photo = await asJpeg(frame, null);
  clock?.lap('encode');
  if (clock) clock.timing.method = 'frame';
  return photo;
}

/**
 * JPEG, at the size it came. The upload is signed as image/jpeg and the solver
 * reads EXIF orientation from it; a canvas JPEG carries none, which reads as
 * "as viewed" -- right for a camera bolted to the lid, and it keeps the
 * sensor frame the lens is measured in.
 */
async function asJpeg(bitmap: ImageBitmap, original: Blob | null): Promise<Still> {
  const { width, height } = bitmap;
  if (original?.type === 'image/jpeg') {
    bitmap.close();
    return { blob: original, width, height };
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.95));
  if (!blob) throw new Error('the browser could not encode the photograph');
  return { blob, width, height };
}

// A per-workstation convenience, so localStorage; it may be unavailable.
function remembered(): string | null {
  try { return localStorage.getItem(DEVICE_KEY); } catch { return null; }
}
function remember(id: string | null) {
  try { if (id) localStorage.setItem(DEVICE_KEY, id); } catch { /* fine */ }
}
